import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Flex,
    Icon,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    SimpleGrid,
    Stack,
    Text,
    useDisclosure,
} from '@chakra-ui/react';
import { countBy, uniqBy } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RiBook2Line } from 'react-icons/ri';

import { PortfolioDrawer, usePortfolioDrawerControls } from '@/cip/Portfolio/PortfolioDrawer/PortfolioDrawer';

import { type EnrichedOptimizerTrade, type OptimizationMeta } from '../../../_openapi/portfolio-optimizer';
import { useGenerateFidelityEquityTradesCsv, useGenerateSchwabEquityTradesCsv } from '../../../api/trade';
import { AssetDrawer, useAssetDrawerControls } from '../../../Asset/AssetDrawer/AssetDrawer';
import { useRequiredParams } from '../../../utils/useRequiredParams';
import { PageContent } from '../../../widgets/PageContent';
import { SegmentedControls } from '../../../widgets/SegmentedControls';
import { MoneyCell } from '../../../widgets/Table/MoneyCell';
import { NumberCell } from '../../../widgets/Table/NumberCell';
import { AdvancedViewDrawer, useAdvancedViewDrawerControls } from './AdvancedViewDrawer';
import { AggregatedBondTradesDrawer, useAggregatedBondTradesDrawerControls } from './AggregatedBondTradesDrawer';
import { BlockTradesDrawer, useBlockTradesDrawerControls } from './BlockTradesDrawer';
import { ExcludedTrades } from './ExcludedTrades';
import {
    SchwabEquityBlockTradesDrawer,
    useSchwabEquityBlockTradesDrawerControls,
} from './SchwabEquityBlockTradesDrawer';
import { TradeDetailsDrawer, useTradeDetailsDrawerControls } from './TradeDetailsDrawer';
import { TradesByAllocationType } from './TradesByAllocationType';
import { TradesByAssetClass } from './TradesByAssetClass';
import { TradesByPortfolio } from './TradesByPortfolio';
import { TradesBySecurity } from './TradesBySecurity';
import { TradesBySide } from './TradesBySide';

enum BrokerId {
    IB = 'IB',
    SCHWAB = 'SCHWAB',
    FIDELITY = 'FIDELITY',
}

export type GroupingColumn = 'portfolioGuid' | 'side' | 'allocationType' | 'symbol' | 'assetClass';
type GroupingOption = { value: GroupingColumn; label: string };

const GROUPING_OPTIONS: GroupingOption[] = [
    { value: 'portfolioGuid', label: 'Portfolio' },
    { value: 'side', label: 'Side' },
    { value: 'allocationType', label: 'Allocation Type' },
    { value: 'symbol', label: 'Security' },
    { value: 'assetClass', label: 'Asset Class' },
];

export const TradeExecution = ({
    trades,
    isLoading,
    optimizationMeta,
}: {
    trades?: EnrichedOptimizerTrade[];
    isLoading: boolean;
    optimizationMeta?: OptimizationMeta;
}) => {
    const { optimizationId } = useRequiredParams(['optimizationId']);

    const { isOpen, onOpen, onClose } = useDisclosure();
    const { viewAsset, ...assetDrawerProps } = useAssetDrawerControls();
    const { viewPortfolio, ...portfolioDrawerProps } = usePortfolioDrawerControls();
    const { viewTrade, ...tradeDetailsDrawerProps } = useTradeDetailsDrawerControls();
    const { viewAggregatedBondTrades, ...aggregatedBondTradesDrawerProps } = useAggregatedBondTradesDrawerControls();
    const { viewBlockTrades, ...blockTradesDrawerProps } = useBlockTradesDrawerControls();
    const { viewSchwabEquityBlockTrades, ...schwabEquityBlockTradesDrawerProps } =
        useSchwabEquityBlockTradesDrawerControls();
    const { viewAdvanced, ...advancedViewDrawerProps } = useAdvancedViewDrawerControls();

    const { mutate: generateSchwabEquityTradesCsv, isPending: isSchwabDownloadPending } =
        useGenerateSchwabEquityTradesCsv();
    const { mutate: generateFidelityEquityTradesCsv, isPending: isFidelityDownloadPending } =
        useGenerateFidelityEquityTradesCsv();

    const [selectedBrokerId, setSelectedBrokerId] = useState<BrokerId>();
    const [grouping, setGrouping] = useState<GroupingColumn>('portfolioGuid');
    const [secondaryGrouping, setSecondaryGrouping] = useState<GroupingColumn>();
    const [excludedTrades, setExcludedTrades] = useState<EnrichedOptimizerTrade[]>([]);

    const excludedTradeIds = useMemo(() => new Set(excludedTrades.map((t) => t.tradeId)), [excludedTrades]);

    const brokerTradeCounts = useMemo(() => countBy(trades ?? [], (t) => t.brokerId), [trades]);

    const selectedBrokerTotal = selectedBrokerId ? (brokerTradeCounts[selectedBrokerId] ?? 0) : (trades?.length ?? 0);

    const selectedBrokerExcludedCount = useMemo(() => {
        if (!selectedBrokerId) return excludedTrades.length;
        return excludedTrades.filter((t) => t.brokerId === selectedBrokerId.valueOf()).length;
    }, [excludedTrades, selectedBrokerId]);

    const filteredTrades = useMemo(() => {
        if (!trades) return [];
        return trades.filter(
            (trade) =>
                (!selectedBrokerId || trade.brokerId === selectedBrokerId.valueOf()) &&
                !excludedTradeIds.has(trade.tradeId),
        );
    }, [excludedTradeIds, trades, selectedBrokerId]);

    const excludeTrades = useCallback(
        (tradesToExclude: EnrichedOptimizerTrade[]) => setExcludedTrades((current) => [...current, ...tradesToExclude]),
        [],
    );

    const removeExclusion = useCallback(
        (trade: EnrichedOptimizerTrade) =>
            setExcludedTrades((current) => current.filter((t) => t.tradeId !== trade.tradeId)),
        [],
    );

    useEffect(() => {
        setSecondaryGrouping(undefined);
    }, [grouping]);

    return (
        <PageContent isLoading={isLoading}>
            <Card size="sm">
                <CardBody>
                    <Flex justifyContent="space-between" minHeight="80px">
                        <Stack>
                            <Text>Broker selection</Text>
                            <SegmentedControls
                                selectedValue={selectedBrokerId}
                                onClick={setSelectedBrokerId}
                                options={[
                                    { value: undefined, label: 'All' },
                                    {
                                        value: BrokerId.IB,
                                        label: 'Interactive Brokers',
                                        badge: brokerTradeCounts[BrokerId.IB] ?? 0,
                                        disabled: !brokerTradeCounts[BrokerId.IB],
                                    },
                                    {
                                        value: BrokerId.SCHWAB,
                                        label: 'Schwab',
                                        badge: brokerTradeCounts[BrokerId.SCHWAB] ?? 0,
                                        disabled: !brokerTradeCounts[BrokerId.SCHWAB],
                                    },
                                    {
                                        value: BrokerId.FIDELITY,
                                        label: 'Fidelity',
                                        badge: brokerTradeCounts[BrokerId.FIDELITY] ?? 0,
                                        disabled: !brokerTradeCounts[BrokerId.FIDELITY],
                                    },
                                ]}
                            />
                            <Text fontSize="xs" color="gray.500" minH="16px">
                                {`${selectedBrokerTotal} total — ${selectedBrokerTotal - selectedBrokerExcludedCount} included & ${selectedBrokerExcludedCount} excluded`}
                            </Text>
                        </Stack>
                        <Stack width="280px" gap={2}>
                            {selectedBrokerId === BrokerId.IB && (
                                <Button size="sm" colorScheme="teal" onClick={onOpen}>
                                    Submit Interactive Brokers trades
                                </Button>
                            )}
                            {selectedBrokerId === BrokerId.SCHWAB && (
                                <>
                                    <Button
                                        size="sm"
                                        colorScheme="teal"
                                        isLoading={isSchwabDownloadPending}
                                        onClick={() =>
                                            generateSchwabEquityTradesCsv({
                                                optimizationId: Number(optimizationId),
                                                requestBody: excludedTrades.map((trade) => trade.tradeId),
                                            })
                                        }
                                    >
                                        Download Schwab equity trades CSV
                                    </Button>
                                    <Button colorScheme="teal" size="sm" onClick={viewSchwabEquityBlockTrades} gap={2}>
                                        <Icon boxSize={4} as={RiBook2Line} />
                                        Open equity block trades
                                    </Button>
                                    <Button colorScheme="teal" size="sm" onClick={viewAggregatedBondTrades} gap={2}>
                                        <Icon boxSize={4} as={RiBook2Line} />
                                        Open aggregated bond trades
                                    </Button>
                                </>
                            )}
                            {selectedBrokerId === BrokerId.FIDELITY && (
                                <>
                                    <Button
                                        size="sm"
                                        colorScheme="teal"
                                        isLoading={isFidelityDownloadPending}
                                        onClick={() =>
                                            generateFidelityEquityTradesCsv({
                                                optimizationId: Number(optimizationId),
                                                requestBody: excludedTrades.map((trade) => trade.tradeId),
                                            })
                                        }
                                    >
                                        Download Fidelity equity trades CSV
                                    </Button>
                                    <Button colorScheme="teal" size="sm" onClick={viewBlockTrades} gap={2}>
                                        <Icon boxSize={4} as={RiBook2Line} />
                                        Open Bond Block trades
                                    </Button>
                                </>
                            )}
                        </Stack>
                    </Flex>
                    <Flex gap={12} borderTopWidth={1} borderBottomWidth={1} py={3} my={3}>
                        <Stack gap={0}>
                            <Text fontSize="small">Primary grouping</Text>
                            <SegmentedControls
                                selectedValue={grouping}
                                onClick={setGrouping}
                                options={GROUPING_OPTIONS}
                            />
                        </Stack>
                        <Stack gap={0}>
                            <Text fontSize="small">Secondary grouping</Text>
                            <SegmentedControls
                                selectedValue={secondaryGrouping}
                                onClick={setSecondaryGrouping}
                                options={[{ value: undefined, label: 'None' }, ...GROUPING_OPTIONS].map((option) => {
                                    const g = GROUPING_OPTIONS.find(({ value }) => value === grouping);
                                    return { ...option, disabled: option.label === g?.label };
                                })}
                            />
                        </Stack>
                    </Flex>
                    <Table
                        grouping={grouping}
                        secondaryGrouping={secondaryGrouping}
                        filteredTrades={filteredTrades}
                        viewAsset={viewAsset}
                        viewPortfolio={viewPortfolio}
                        viewTrade={viewTrade}
                        excludeTrades={excludeTrades}
                        viewAdvanced={viewAdvanced}
                    />
                </CardBody>
            </Card>

            {!!excludedTrades.length && (
                <Card mt={4} size="sm">
                    <CardHeader px={5} py={3} borderBottomWidth={1}>
                        Excluded Trades ({excludedTrades.length})
                    </CardHeader>
                    <CardBody>
                        <ExcludedTrades
                            trades={excludedTrades}
                            removeExclusion={removeExclusion}
                            viewAsset={viewAsset}
                            viewPortfolio={viewPortfolio}
                        />
                    </CardBody>
                </Card>
            )}

            <Modal isOpen={isOpen} onClose={onClose} size="2xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Submit trades</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Stack>
                            <Text>Are you sure you want to submit?</Text>

                            <SimpleGrid columnGap={10} gridTemplateColumns="max-content max-content">
                                <Text>Total number of trades</Text>
                                <NumberCell value={filteredTrades.length} fractionDigits={0} />
                                <Text>Total number of portfolios</Text>
                                <NumberCell value={uniqBy(filteredTrades, 'pId').length} fractionDigits={0} />
                                <Text>Total number of shares</Text>
                                <NumberCell
                                    value={filteredTrades.reduce(
                                        (result, trade) => result + Math.abs(trade.tradeQuantity),
                                        0,
                                    )}
                                    fractionDigits={0}
                                />
                                <Text>Total trade value</Text>
                                <MoneyCell
                                    value={filteredTrades.reduce(
                                        (result, trade) => result + (trade.tradeValue ?? 0),
                                        0,
                                    )}
                                />
                            </SimpleGrid>
                        </Stack>
                    </ModalBody>

                    <ModalFooter justifyContent="space-between">
                        <Button variant="ghost" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button
                            colorScheme="teal"
                            isLoading={false}
                            onClick={() => {
                                // TODO: Enable trade submission for IB trades
                            }}
                            isDisabled
                        >
                            Good to send!
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <PortfolioDrawer {...portfolioDrawerProps} />
            <AssetDrawer {...assetDrawerProps} />
            <TradeDetailsDrawer {...tradeDetailsDrawerProps} />
            <AggregatedBondTradesDrawer
                optimizationId={Number(optimizationId)}
                excludedOrderIds={excludedTrades.map((trade) => trade.tradeId)}
                {...aggregatedBondTradesDrawerProps}
            />
            <BlockTradesDrawer
                optimizationId={Number(optimizationId)}
                excludedOrderIds={excludedTrades.map((trade) => trade.tradeId)}
                {...blockTradesDrawerProps}
            />
            <SchwabEquityBlockTradesDrawer
                optimizationId={Number(optimizationId)}
                excludedOrderIds={excludedTrades.map((trade) => trade.tradeId)}
                {...schwabEquityBlockTradesDrawerProps}
            />
            <AdvancedViewDrawer
                optimizationId={Number(optimizationId)}
                optimizationMeta={optimizationMeta}
                {...advancedViewDrawerProps}
            />
        </PageContent>
    );
};

const Table = ({
    grouping,
    secondaryGrouping,
    filteredTrades,
    excludeTrades,
    viewAsset,
    viewPortfolio,
    viewTrade,
    viewAdvanced,
}: {
    grouping: GroupingColumn;
    secondaryGrouping?: GroupingColumn;
    filteredTrades: EnrichedOptimizerTrade[] | undefined;
    excludeTrades: (trades: EnrichedOptimizerTrade[]) => void;
    viewAsset: (sId: string) => void;
    viewPortfolio: (pId: string) => void;
    viewTrade: (trade: EnrichedOptimizerTrade) => void;
    viewAdvanced: (portfolioGuid: string, pId: string) => void;
}) => {
    switch (grouping) {
        case 'portfolioGuid':
            return (
                <TradesByPortfolio
                    secondaryGrouping={secondaryGrouping}
                    filteredTrades={filteredTrades}
                    excludeTrades={excludeTrades}
                    viewAsset={viewAsset}
                    viewPortfolio={viewPortfolio}
                    viewTrade={viewTrade}
                    viewAdvanced={viewAdvanced}
                />
            );
        case 'side':
            return (
                <TradesBySide
                    secondaryGrouping={secondaryGrouping}
                    filteredTrades={filteredTrades}
                    excludeTrades={excludeTrades}
                    viewAsset={viewAsset}
                    viewPortfolio={viewPortfolio}
                    viewTrade={viewTrade}
                    viewAdvanced={viewAdvanced}
                />
            );
        case 'allocationType':
            return (
                <TradesByAllocationType
                    secondaryGrouping={secondaryGrouping}
                    filteredTrades={filteredTrades}
                    excludeTrades={excludeTrades}
                    viewAsset={viewAsset}
                    viewPortfolio={viewPortfolio}
                    viewTrade={viewTrade}
                    viewAdvanced={viewAdvanced}
                />
            );
        case 'symbol':
            return (
                <TradesBySecurity
                    secondaryGrouping={secondaryGrouping}
                    filteredTrades={filteredTrades}
                    excludeTrades={excludeTrades}
                    viewAsset={viewAsset}
                    viewPortfolio={viewPortfolio}
                    viewTrade={viewTrade}
                    viewAdvanced={viewAdvanced}
                />
            );
        case 'assetClass':
            return (
                <TradesByAssetClass
                    secondaryGrouping={secondaryGrouping}
                    filteredTrades={filteredTrades}
                    excludeTrades={excludeTrades}
                    viewAsset={viewAsset}
                    viewPortfolio={viewPortfolio}
                    viewTrade={viewTrade}
                    viewAdvanced={viewAdvanced}
                />
            );
    }
};
